//@ts-nocheck
import React, { useEffect, useMemo, useState, useLocation } from "react";
import { Table, Button, Input, Switch, Select, message } from "antd";
import { LeftOutlined, PlusOutlined, SearchOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import { useNavigate } from "react-router-dom";
import { RiDeleteBinLine } from "react-icons/ri";
import {
  createCompany,
  deleteCompany,
  getCompanies,
  getNextCompanyId,
  updateCompany,
  type CompanyResponse,
} from "../../utils/API";
import NavigationDropdown from "../../Components/NavigationDropDown";
import PageLogoutButton from "../../Components/PageLogoutButton";

type Company = {
  id?: number;
  localId: number;
  name: string;
  website: string;
  reviewDone: boolean;
  isAsia: boolean;
  quotesCount: number | "";
};

type FilterMode = "all" | "done" | "not_done";

export default function Company() {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [filter, setFilter] = useState<FilterMode>("all");
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 1,
  });
  const navigate = useNavigate();

  const mapCompany = (item: CompanyResponse): Company => ({
    id: item.id,
    localId: item.id,
    name: item.name || "",
    website: item.website || "",
    reviewDone: Number(item.review || 0) > 0,
    isAsia: Boolean(item.isAsia),
    quotesCount: (() => {
      if (item.quotesCount === null || item.quotesCount === undefined) return "";
      const raw = String(item.quotesCount).trim();
      if (!raw) return "";
      const parsed = Number(raw);
      return Number.isFinite(parsed) ? parsed : "";
    })(),
  });

  const fetchCompanies = async (page = pagination.page) => {
    try {
      setLoading(true);
      const response = await getCompanies({
        page,
        limit: pagination.limit,
        search: searchText.trim() || undefined,
        review: filter === "all" ? undefined : filter,
      });
      setCompanies(response.data.map(mapCompany));
      setPagination((prev) => ({
        ...prev,
        page: response.pagination.page,
        limit: response.pagination.limit,
        total: response.pagination.total,
        totalPages: response.pagination.totalPages,
      }));
    } catch (error: any) {
      message.error(error?.response?.data?.message || "Failed to fetch companies");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchCompanies(pagination.page);
    }, 300);
    return () => clearTimeout(timer);
  }, [pagination.page, searchText, filter]);

  const getNextCompanyLocalId = async () => {
    try {
      const response = await getNextCompanyId();
      return Number(response.nextId || 1);
    } catch (error: any) {
      message.error(error?.response?.data?.message || "Failed to get next id");
      const fallbackMax = companies.reduce(
        (max, item) => Math.max(max, Number(item.localId || 0)),
        0
      );
      return fallbackMax + 1;
    }
  };

  const updateCompanyLocal = (localId: number, patch: Partial<Company>) => {
    setCompanies((prev) =>
      prev.map((c) => (c.localId === localId ? { ...c, ...patch } : c)),
    );
  };

  const persistCompany = async (company: Company) => {
    const name = company.name.trim();
    if (!name) {
      message.warning("Company name is required");
      return;
    }

    try {
      if (!company.id) {
        const quotesCount =
          company.quotesCount === "" ? null : String(company.quotesCount);
        const created = await createCompany({
          id: company.localId,
          name,
          website: company.website.trim() || null,
          review: company.reviewDone ? 1 : 0,
          quotesCount,
          isAsia: company.isAsia,
        });
        setCompanies((prev) =>
          prev.map((c) =>
            c.localId === company.localId ? { ...c, id: created.id } : c
          )
        );
        return;
      }

      const quotesCount =
        company.quotesCount === "" ? null : String(company.quotesCount);
      await updateCompany(company.id, {
        name,
        website: company.website.trim() || null,
        review: company.reviewDone ? 1 : 0,
        quotesCount,
        isAsia: company.isAsia,
      });
    } catch (error: any) {
      message.error(error?.response?.data?.message || "Failed to save company");
    }
  };

  const addCompany = async () => {
    const nextLocalId = await getNextCompanyLocalId();
    const tempCompany: Company = {
      id: undefined,
      localId: nextLocalId,
      name: "",
      website: "",
      reviewDone: false,
      isAsia: false,
      quotesCount: "",
    };
    setCompanies((prev) => [tempCompany, ...prev]);
  };

  const deleteCompanyRow = async (company: Company) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this company?",
    );
    if (!confirmDelete) return;

    try {
      if (!company.id) {
        setCompanies((prev) =>
          prev.filter((c) => c.localId !== company.localId),
        );
        return;
      }
      await deleteCompany(company.id);
      setCompanies((prev) => prev.filter((c) => c.id !== company.id));
      // message.success("Company deleted successfully");
    } catch (error: any) {
      message.error(error?.response?.data?.message || "Failed to delete company");
    }
  };

  const filtered = useMemo(() => companies, [companies]);

  const columns: ColumnsType<Company> = [
    {
      title: "#",
      key: "index",
      align: "left",
      width: "5%",
      render: (_, __, index) => index + 1,
      onHeaderCell: () => ({
        style: {
          backgroundColor: "#DFEFFF",
          color: "black",
          fontWeight: "600",
          paddingLeft: "40px",
          fontSize: "14px",
        },
      }),
      onCell: () => ({
        style: { paddingLeft: "40px", fontWeight: "400" },
      }),
    },
    {
      title: "ID",
      dataIndex: "localId",
      key: "localId",
      align: "left",
      width: "8%",
      onHeaderCell: () => ({
        style: {
          backgroundColor: "#DFEFFF",
          color: "black",
          fontWeight: "600",
          fontSize: "14px",
        },
      }),
      onCell: () => ({
        style: { fontWeight: "400" },
      }),
      render: (localId: number) => (
        <Input value={String(localId)} disabled className="w-full rounded-lg" />
      ),
    },
    {
      title: "Company Name",
      dataIndex: "name",
      key: "name",
      align: "left",
      width: "25%",
      onHeaderCell: () => ({
        style: {
          backgroundColor: "#DFEFFF",
          color: "black",
          fontWeight: "600",
          fontSize: "14px",
        },
      }),
      onCell: () => ({
        style: { fontWeight: "400" },
      }),
      render: (name: string, record: Company) => (
        <Input
          value={name}
          onChange={(e) =>
            updateCompanyLocal(record.localId, { name: e.target.value })
          }
          onBlur={() => persistCompany(record)}
          className="w-full rounded-lg"
          size="middle"
        />
      ),
    },
    {
      title: "Website",
      dataIndex: "website",
      key: "website",
      align: "left",
      width: "25%",
      onHeaderCell: () => ({
        style: {
          backgroundColor: "#DFEFFF",
          color: "black",
          fontWeight: "600",
          fontSize: "14px",
        },
      }),
      onCell: () => ({
        style: { fontWeight: "400" },
      }),
      render: (website: string, record: Company) => (
        <Input
          value={website}
          placeholder="www.example.com"
          onChange={(e) =>
            updateCompanyLocal(record.localId, { website: e.target.value })
          }
          onBlur={() => persistCompany(record)}
          className="w-full rounded-lg"
          size="middle"
        />
      ),
    },
    {
      title: "Review Done",
      dataIndex: "reviewDone",
      key: "reviewDone",
      align: "center",
      width: "15%",
      onHeaderCell: () => ({
        style: {
          backgroundColor: "#DFEFFF",
          color: "black",
          fontWeight: "600",
          fontSize: "14px",
        },
      }),
      render: (reviewDone: boolean, record: Company) => (
        <Switch
          checked={reviewDone}
          onChange={(checked) => {
            const next = { ...record, reviewDone: checked };
            updateCompanyLocal(record.localId, { reviewDone: checked });
            if (record.id) {
              persistCompany(next);
            }
          }}
          className="bg-gray-200"
        />
      ),
    },
    {
      title: "Is Asia",
      dataIndex: "isAsia",
      key: "isAsia",
      align: "center",
      width: "15%",
      onHeaderCell: () => ({
        style: {
          backgroundColor: "#DFEFFF",
          color: "black",
          fontWeight: "600",
          fontSize: "14px",
        },
      }),
      render: (isAsia: boolean, record: Company) => (
        <Switch
          checked={isAsia}
          onChange={(checked) => {
            const next = { ...record, isAsia: checked };
            updateCompanyLocal(record.localId, { isAsia: checked });
            if (record.id) {
              persistCompany(next);
            }
          }}
          className="bg-gray-200"
        />
      ),
    },
    {
      title: "Quotes Count",
      dataIndex: "quotesCount",
      key: "quotesCount",
      align: "left",
      width: "10%",
      onHeaderCell: () => ({
        style: {
          backgroundColor: "#DFEFFF",
          color: "black",
          fontWeight: "600",
          fontSize: "14px",
        },
      }),
      onCell: () => ({
        style: { fontWeight: "400" },
      }),
      render: (quotesCount: number | "", record: Company) => (
        <Input
          value={quotesCount === "" ? "" : String(quotesCount)}
          placeholder="-"
          onChange={(e) => {
            const digits = e.target.value.replace(/[^\d]/g, "");
            const nextValue = digits === "" ? "" : Number(digits);
            updateCompanyLocal(record.localId, { quotesCount: nextValue });
          }}
          onBlur={() => persistCompany(record)}
          className="w-full rounded-lg"
          size="middle"
        />
      ),
    },
    {
      title: "Action",
      key: "action",
      align: "center",
      width: "15%",
      onHeaderCell: () => ({
        style: {
          backgroundColor: "#DFEFFF",
          color: "black",
          fontWeight: "600",
          paddingRight: "40px",
          fontSize: "14px",
        },
      }),
      onCell: () => ({
        style: { paddingRight: "40px" },
      }),
      render: (_, record: Company) => (
        <div className="flex justify-center gap-3">
          <Button
            type="primary"
            size="small"
            
            className={`rounded-lg bg-[#004aad] text-white `}
            // onClick={() => alert(`Manage Contact: ${record.name}`)}
          >
            Manage Contact
          </Button>
          <Button
            type="text"
            danger
            size="small"
            className="flex items-center justify-center p-1 hover:bg-red-50"
            onClick={() => deleteCompanyRow(record)}
            title="Delete"
          >
            <RiDeleteBinLine className="text-gray-600 hover:text-red-600 text-lg" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="p-2 bg-slate-50 min-h-screen">
      <div className="flex justify-end mb-2">
        <PageLogoutButton />
      </div>
      {/* Header Section */}
      <div className="flex justify-between items-center p-2 mb-4 gap-2">
        {/* Back + Dropdown + Heading */}
        <div className="flex items-center gap-4">
          <Button
            icon={<LeftOutlined />}
            size="small"
            className="flex items-center justify-center border-gray-300 rounded-lg"
            onClick={() => navigate("/ate-dashboard")}
          />

          <NavigationDropdown />

          <h1 className="text-base sm:text-xl font-normal text-gray-700">
            Company Master
          </h1>
        </div>

        {/* Search and Filter Buttons */}
        <div className="flex flex-1 justify-end gap-4 items-center">
          <div className="flex gap-2">
            <Button
              type={"primary"}
              className={`rounded-lg bg-[#004aad]`}
              onClick={() => {
                setFilter("not_done");
                setPagination((prev) => ({ ...prev, page: 1 }));
              }}
            >
              Find Not Done
            </Button>
            <Button
              type={"primary"}
              className={`rounded-lg bg-[#004aad]`}
              onClick={() => {
                setFilter("done");
                setPagination((prev) => ({ ...prev, page: 1 }));
              }}
            >
              Find Done
            </Button>
            <Button
              type={"primary"}
              className={`rounded-lg bg-[#004aad]`}
              onClick={() => {
                setFilter("all");
                setPagination((prev) => ({ ...prev, page: 1 }));
              }}
            >
              Show All
            </Button>
          </div>

          <Input
            prefix={<SearchOutlined className="text-xl" />}
            placeholder="Search ...."
            className="rounded-lg h-10 border-gray-300 flex-1 max-w-xs"
            value={searchText}
            onChange={(e) => {
              setSearchText(e.target.value);
              setPagination((prev) => ({ ...prev, page: 1 }));
            }}
          />
          <Button
            type="primary"
            className="bg-[#004aad] hover:bg-blue-800 rounded-lg border-none flex items-center justify-center font-light flex-shrink-0 text-white h-8 w-8 sm:h-10 sm:w-auto sm:px-6"
            onClick={addCompany}
          >
            <PlusOutlined className="text-base sm:text-lg" />
            <span className="!hidden sm:!inline ml-2">Add</span>
          </Button>
        </div>
      </div>

      {/* Table Section */}
      <div className="bg-white rounded-[24px] border border-gray-200 p-6 shadow-sm">
        <div className="rounded-lg overflow-hidden mx-3">
          <Table
            dataSource={filtered}
            columns={columns}
            pagination={{
              current: pagination.page,
              pageSize: pagination.limit,
              total: pagination.total,
              showSizeChanger: false,
              onChange: (page) => setPagination((prev) => ({ ...prev, page })),
            }}
            loading={loading}
            className="custom-table"
            rowKey="localId"
            rowClassName="h-16"
            locale={{
              emptyText: (
                <div className="py-8 text-center text-gray-500">
                  No companies found.
                </div>
              ),
            }}
          />
        </div>
      </div>
    </div>
  );
}
